import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Container, Grid, Typography,
  Stack, Paper, Chip
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VerifiedIcon from '@mui/icons-material/Verified';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import StarIcon from '@mui/icons-material/Star';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

// ─── Animations ───────────────────────────────────────────────────────────────

const marquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const float = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-12px); }
  100% { transform: translateY(0px); }
`;

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.8) translateY(20px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

// ─── Colour tokens ────────────────────────────────────────────────────────────

const HOT_PINK  = '#FF1E8C';
const YELLOW    = '#FFD600';
const ORANGE    = '#FF6B35';
const GREEN     = '#00D97E';
const DARK_CHAR = '#1C1C1C';
const PURPLE    = '#9B5DE5';

// ─── Rotation constants ───────────────────────────────────────────────────────

const ROTATIONS = [2, -2, -1.5, 2.5];

// ─── Creator photo grid data ──────────────────────────────────────────────────

const CREATORS = [
  {
    name: 'Priya S.',
    niche: 'Fashion',
    bg: ORANGE,
    img: 'https://images.pexels.com/photos/6977987/pexels-photo-6977987.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop',
  },
  {
    name: 'Ananya R.',
    niche: 'Lifestyle',
    bg: PURPLE,
    img: 'https://images.pexels.com/photos/16461275/pexels-photo-16461275.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop',
  },
  {
    name: 'Kavya M.',
    niche: 'Beauty',
    bg: '#f5f0ff',
    img: 'https://images.pexels.com/photos/26844139/pexels-photo-26844139.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop',
  },
  {
    name: 'Rohit K.',
    niche: 'Tech',
    bg: GREEN,
    img: 'https://images.pexels.com/photos/16659308/pexels-photo-16659308.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop',
  },
];

const BRAND_NAMES = [
  'రుద్ర ఫ్యామిలీ జిమ్', 'ELEVATE COUTURE', 'కావేరి షాపింగ్ సెంటర్', 'అన్నపూర్ణి రసోఈ',
  'సాత్విక భోజన', 'LUPINE FITNESS CLUB', 'సుప్రభాత భోజనం', 'ORCHARD POINT MALL',
  'హంస బట్టిమనె', 'HARMONY ACCESSORIES', 'PRIME GYM & FITNESS', 'ప్రగతి షాపింగ్ మాల్',
  'రుద్ర ఫ్యామిలీ జిమ్', 'ELEVATE COUTURE', 'కావేరి షాపింగ్ సెంటర్', 'అన్నపూర్ణి రసోఈ',
  'సాత్విక భోజన', 'LUPINE FITNESS CLUB', 'సుప్రభాత భోజనం', 'ORCHARD POINT MALL',
  'హంస బట్టిమనె', 'HARMONY ACCESSORIES', 'PRIME GYM & FITNESS', 'ప్రగతి షాపింగ్ మాల్',
];

const STATS = [
  { number: '700+', label: 'Happy Clients',      icon: <CampaignIcon />,     color: ORANGE   },
  { number: '900+', label: 'Top Influencers',    icon: <PeopleIcon />,       color: HOT_PINK },
  { number: '₹16Cr+', label: 'Creator Earnings', icon: <AttachMoneyIcon />, color: GREEN    },
  { number: '98%',  label: 'Success Rate',       icon: <VerifiedIcon />,    color: PURPLE   },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    icon: <CampaignIcon />,
    title: 'Launch a Campaign',
    desc: 'Tell us your goals, budget, and target audience. Takes under 5 minutes.',
    accent: YELLOW,
  },
  {
    step: '02',
    icon: <AutoAwesomeIcon />,
    title: 'Match with Creators',
    desc: 'Browse our verified creator pool or let our algorithm find the perfect fit.',
    accent: HOT_PINK,
  },
  {
    step: '03',
    icon: <RocketLaunchIcon />,
    title: 'Grow & Measure',
    desc: 'Track real results — reach, engagement, and conversions — in real time.',
    accent: GREEN,
  },
];

const FEATURES = [
  { icon: <VerifiedIcon />,     title: 'Verified Creators',    desc: '100% authentic — zero fake followers, zero vanity metrics.', color: HOT_PINK },
  { icon: <FlashOnIcon />,      title: 'Instant Payments',     desc: 'Creators get paid within 48 hours. No invoice chasing.',     color: YELLOW   },
  { icon: <RocketLaunchIcon />, title: 'AI-Powered Matching',  desc: 'Smart algorithm connects brands with the perfect audience.',  color: ORANGE   },
  { icon: <StarIcon />,         title: '24/7 Support',         desc: 'Dedicated success team with you from launch to results.',     color: PURPLE   },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const Home = () => {
  const theme  = useTheme();
  const dark   = theme.palette.mode === 'dark';

  // Indian flag radial blob gradient — saffron top-left, green right, pink accent bottom-left
  const bg = dark
    ? '#0a0a0a'
    : [
        'radial-gradient(ellipse 70% 55% at 4%  10%, rgba(255,153,51,0.38) 0%, transparent 100%)',
        'radial-gradient(ellipse 55% 60% at 95% 30%, rgba(19,136,8,0.18)   0%, transparent 100%)',
        'radial-gradient(ellipse 50% 45% at 8%  88%, rgba(236,72,153,0.20) 0%, transparent 100%)',
        '#ffffff',
      ].join(', ');
  const surface = dark ? '#1a1a1a' : '#ffffff';
  const border  = dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.07)';
  const text    = dark ? '#f1f5f9' : DARK_CHAR;

  // Per-card rotation map for stat cards
  const statRotations = [
    'rotate(-1.5deg)',
    'rotate(1deg)',
    'rotate(-1deg)',
    'rotate(1.5deg)',
  ];

  return (
    <Box sx={{ background: bg, overflowX: 'hidden' }}>

      {/* ════════════════════════════════════════════════
          HERO
      ════════════════════════════════════════════════ */}
      <Box sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 6, md: 4 }} alignItems="center">

            {/* ── Left copy ── */}
            <Grid item xs={12} md={6}>

              {/* Badge */}
              <Chip
                icon={<AutoAwesomeIcon sx={{ color: `${YELLOW} !important`, fontSize: '16px !important' }} />}
                label="India's #1 Creator-Brand Platform"
                sx={{
                  mb: 3,
                  bgcolor: dark ? 'rgba(255,214,0,0.12)' : '#FFF8DC',
                  color: dark ? YELLOW : '#856A00',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  border: `1px solid ${YELLOW}40`,
                  height: 36,
                }}
              />

              {/* Headline */}
              <Typography
                component="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4rem', lg: '4.8rem' },
                  lineHeight: 1.05,
                  letterSpacing: '-1.5px',
                  color: text,
                  mb: 0.5,
                }}
              >
                Connecting Brands
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4rem', lg: '4.8rem' },
                  lineHeight: 1.05,
                  letterSpacing: '-1.5px',
                  color: text,
                  mb: 0.5,
                }}
              >
                with the Right
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4rem', lg: '4.8rem' },
                    lineHeight: 1.05,
                    letterSpacing: '-1.5px',
                    color: HOT_PINK,
                  }}
                >
                  Influencers
                </Typography>
                {/* Yellow accent squiggle */}
                <Box
                  sx={{
                    width: { xs: 32, md: 44 },
                    height: { xs: 32, md: 44 },
                    bgcolor: YELLOW,
                    borderRadius: '50% 10% 50% 10%',
                    flexShrink: 0,
                    animation: `${float} 3s ease-in-out infinite`,
                  }}
                />
              </Box>

              {/* Sub-copy */}
              <Typography
                variant="h6"
                component="p"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  lineHeight: 1.7,
                  maxWidth: 480,
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.15rem' },
                }}
              >
                We help businesses grow with authentic influencer campaigns
                that reach the right audience — guaranteed results or your money back.
              </Typography>

              {/* Stats row */}
              <Stack direction="row" spacing={4} sx={{ mb: 5 }} divider={
                <Box sx={{ width: '1px', bgcolor: dark ? 'rgba(255,255,255,0.12)' : '#e0e0e0', alignSelf: 'stretch' }} />
              }>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: '2rem', color: ORANGE, lineHeight: 1 }}>700+</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Happy Clients</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: '2rem', color: HOT_PINK, lineHeight: 1 }}>900+</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Popular Influencers</Typography>
                </Box>
              </Stack>

              {/* CTA buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: HOT_PINK,
                    color: '#fff',
                    borderRadius: '50px',
                    px: 4,
                    py: 1.75,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: `0 8px 24px ${HOT_PINK}50`,
                    '&:hover': { bgcolor: '#e0177d', boxShadow: `0 12px 32px ${HOT_PINK}60` },
                  }}
                >
                  Let's Start
                </Button>
                <Button
                  component={RouterLink}
                  to="/how-it-works"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderRadius: '50px',
                    px: 4,
                    py: 1.75,
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                    color: text,
                    '&:hover': {
                      borderColor: HOT_PINK,
                      color: HOT_PINK,
                      bgcolor: `${HOT_PINK}08`,
                    },
                  }}
                >
                  How It Works
                </Button>
              </Stack>
            </Grid>

            {/* ── Right photo grid ── */}
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', maxWidth: 520, mx: 'auto' }}>

                {/* Decorative diagonal bar — yellow */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -30,
                    right: -50,
                    width: 16,
                    height: 200,
                    bgcolor: YELLOW,
                    borderRadius: 4,
                    transform: 'rotate(18deg)',
                    opacity: 0.85,
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />

                {/* Decorative diagonal bar — hot pink */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -30,
                    left: -40,
                    width: 16,
                    height: 160,
                    bgcolor: HOT_PINK,
                    borderRadius: 4,
                    transform: 'rotate(-15deg)',
                    opacity: 0.6,
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />

                <Grid container spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                  {CREATORS.map((c, i) => (
                    <Grid item xs={6} key={c.name}>
                      <Box
                        sx={{
                          bgcolor: c.bg,
                          borderRadius: 4,
                          overflow: 'hidden',
                          height: { xs: 160, sm: 200, md: 220 },
                          position: 'relative',
                          animation: `${popIn} 0.6s ease-out ${i * 0.1}s both`,
                          border: '4px solid',
                          borderColor: dark ? '#1a1a1a' : 'rgba(255,255,255,0.75)',
                          transform: `rotate(${ROTATIONS[i]}deg)`,
                          transformOrigin: 'center',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'rotate(0deg) scale(1.03)',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={c.img}
                          alt={c.name}
                          loading="lazy"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                        {/* name overlay */}
                        <Box sx={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
                          px: 1.5, py: 1,
                        }}>
                          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, display: 'block' }}>
                            {c.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
                            {c.niche}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* 900+ floating badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -16,
                    right: -16,
                    bgcolor: YELLOW,
                    borderRadius: '50%',
                    width: 96,
                    height: 96,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 8px 24px ${YELLOW}80`,
                    animation: `${float} 4s ease-in-out infinite`,
                    border: `4px solid ${dark ? '#0a0a0a' : '#ffffff'}`,
                    zIndex: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 900, fontSize: '1.25rem', lineHeight: 1, color: DARK_CHAR }}>900+</Typography>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: DARK_CHAR, textAlign: 'center', lineHeight: 1.2, px: 0.5 }}>
                    popular<br />influencer
                  </Typography>
                </Box>

                {/* Verified badge — rotated like a sticker */}
                <Paper
                  elevation={0}
                  sx={{
                    position: 'absolute',
                    top: -16,
                    left: -16,
                    bgcolor: surface,
                    border,
                    borderRadius: 3,
                    px: 2,
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    animation: `${float} 5s ease-in-out infinite 1s`,
                    transform: 'rotate(-6deg)',
                    zIndex: 2,
                  }}
                >
                  <VerifiedIcon sx={{ color: '#1d9bf0', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}>100% Verified</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Zero fake followers</Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ════════════════════════════════════════════════
          BRAND MARQUEE BAND
          clipPath creates angled top/bottom edges that overlap adjacent sections
      ════════════════════════════════════════════════ */}
      <Box
        sx={{
          bgcolor: DARK_CHAR,
          overflow: 'hidden',
          clipPath: 'polygon(0 18%, 100% 0, 100% 82%, 0 100%)',
          mt: -4,
          mb: -4,
          py: 6,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 6,
            animation: `${marquee} 20s linear infinite`,
            whiteSpace: 'nowrap',
            width: 'max-content',
          }}
        >
          {BRAND_NAMES.map((name, i) => (
            <Typography
              key={i}
              sx={{
                color: i % 3 === 0 ? YELLOW : 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                fontSize: '1rem',
                letterSpacing: '0.15em',
                userSelect: 'none',
              }}
            >
              {name}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════
          STATS
      ════════════════════════════════════════════════ */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: dark ? '#111111' : 'transparent',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {STATS.map((s, i) => (
              <Grid item xs={6} md={3} key={s.label}>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: surface,
                    border,
                    borderRadius: 4,
                    p: 3,
                    textAlign: 'center',
                    transform: statRotations[i],
                    transition: 'transform 0.25s, box-shadow 0.25s',
                    '&:hover': {
                      transform: 'translateY(-6px) rotate(0deg)',
                      boxShadow: `0 16px 40px ${s.color}25`,
                    },
                  }}
                >
                  <Box sx={{ color: s.color, mb: 1.5, '& svg': { fontSize: 36 } }}>{s.icon}</Box>
                  <Typography sx={{ fontWeight: 900, fontSize: '2rem', color: s.color, lineHeight: 1 }}>{s.number}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, fontWeight: 500 }}>{s.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ════════════════════════════════════════════════
          HOW IT WORKS
      ════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>

        {/* Decorative diagonal bar — yellow left edge */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: -40,
            width: 20,
            height: 280,
            bgcolor: YELLOW,
            borderRadius: 6,
            transform: 'rotate(-12deg)',
            opacity: dark ? 0.08 : 0.35,
            pointerEvents: 'none',
          }}
        />

        {/* Decorative diagonal bar — green right edge */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: -40,
            width: 20,
            height: 200,
            bgcolor: GREEN,
            borderRadius: 6,
            transform: 'rotate(12deg)',
            opacity: dark ? 0.08 : 0.3,
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg">
          {/* Section header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Simple Process"
              sx={{ mb: 2, bgcolor: `${HOT_PINK}15`, color: HOT_PINK, fontWeight: 700, border: `1px solid ${HOT_PINK}30` }}
            />
            <Typography
              variant="h2"
              sx={{ fontWeight: 900, color: text, letterSpacing: '-1px', mb: 2, fontSize: { xs: '2rem', md: '2.75rem' } }}
            >
              How InfHire Works
            </Typography>
            <Typography variant="h6" component="p" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', fontWeight: 400 }}>
              From campaign launch to real results in three simple steps.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {HOW_IT_WORKS.map((step, i) => (
              <Grid item xs={12} md={4} key={step.step}>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: surface,
                    border,
                    borderRadius: 4,
                    p: 4,
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.25s, box-shadow 0.25s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 50px ${step.accent}30`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0, left: 0, right: 0,
                      height: 4,
                      bgcolor: step.accent,
                    },
                  }}
                >
                  {/* Step number watermark */}
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: -8, right: 16,
                      fontSize: '5rem',
                      fontWeight: 900,
                      color: `${step.accent}15`,
                      lineHeight: 1,
                      userSelect: 'none',
                    }}
                  >
                    {step.step}
                  </Typography>

                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      bgcolor: `${step.accent}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      '& svg': { fontSize: 28, color: step.accent },
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 800, mb: 1.5, color: text }}>{step.title}</Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{step.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ════════════════════════════════════════════════
          FEATURES GRID
      ════════════════════════════════════════════════ */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: dark ? '#111111' : 'transparent',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative rotated square — orange outline */}
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            right: -30,
            width: 80,
            height: 80,
            border: '12px solid',
            borderColor: `${ORANGE}40`,
            borderRadius: 4,
            transform: 'rotate(30deg)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Why InfHire"
              sx={{ mb: 2, bgcolor: `${ORANGE}15`, color: ORANGE, fontWeight: 700, border: `1px solid ${ORANGE}30` }}
            />
            <Typography
              variant="h2"
              sx={{ fontWeight: 900, color: text, letterSpacing: '-1px', mb: 2, fontSize: { xs: '2rem', md: '2.75rem' } }}
            >
              Built for Real Results
            </Typography>
            <Typography variant="h6" component="p" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', fontWeight: 400 }}>
              Everything a brand or creator needs — in one powerful platform.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {FEATURES.map((f, i) => (
              <Grid item xs={12} sm={6} key={f.title}>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: surface,
                    border,
                    borderRadius: 4,
                    p: 3.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2.5,
                    transition: 'transform 0.25s, box-shadow 0.25s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 32px ${f.color}20` },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2.5,
                      bgcolor: `${f.color}18`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      '& svg': { fontSize: 26, color: f.color },
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 800, mb: 0.5, color: text }}>{f.title}</Typography>
                    <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>{f.desc}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ════════════════════════════════════════════════
          PLATFORM ICONS STRIP
      ════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="sm">
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3, fontWeight: 500 }}>
            Works with your favourite platforms
          </Typography>
          <Stack direction="row" spacing={4} justifyContent="center" alignItems="center">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <YouTubeIcon sx={{ fontSize: 40, color: '#FF0000' }} />
              <Typography variant="caption" color="text.secondary">YouTube</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <InstagramIcon sx={{ fontSize: 40, color: '#E4405F' }} />
              <Typography variant="caption" color="text.secondary">Instagram</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 40, height: 40,
                  bgcolor: dark ? '#fff' : DARK_CHAR,
                  borderRadius: 1.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Typography sx={{ color: dark ? DARK_CHAR : '#fff', fontWeight: 900, fontSize: '0.8rem', letterSpacing: '-0.5px' }}>
                  TikTok
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">TikTok</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 40, height: 40,
                  bgcolor: '#1877F2',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.2rem' }}>f</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Facebook</Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ════════════════════════════════════════════════
          FINAL CTA
          clipPath creates angled top edge
      ════════════════════════════════════════════════ */}
      <Box
        sx={{
          bgcolor: DARK_CHAR,
          position: 'relative',
          overflow: 'hidden',
          clipPath: 'polygon(0 5%, 100% 0, 100% 100%, 0 100%)',
          mt: -4,
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 14 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -80, left: '50%',
            transform: 'translateX(-50%)',
            width: 600, height: 600,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${HOT_PINK}30 0%, transparent 70%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          {/* Yellow accent dot */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: YELLOW, mr: 1 }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: HOT_PINK, mr: 1 }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: GREEN }} />
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              color: '#fff',
              letterSpacing: '-1px',
              mb: 1,
              fontSize: { xs: '2rem', md: '3.5rem' },
            }}
          >
            Ready to{' '}
            <Box component="span" sx={{ color: HOT_PINK }}>Grow</Box>?
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{
              color: 'rgba(255,255,255,0.65)',
              mb: 5,
              fontWeight: 400,
              maxWidth: 480,
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Join thousands of brands and creators already growing together on InfHire.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: HOT_PINK,
                color: '#fff',
                borderRadius: '50px',
                px: 5,
                py: 1.75,
                fontSize: '1.05rem',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: `0 8px 24px ${HOT_PINK}60`,
                '&:hover': { bgcolor: '#e0177d' },
              }}
            >
              Start for Free
            </Button>
            <Button
              component={RouterLink}
              to="/how-it-works"
              variant="outlined"
              size="large"
              sx={{
                borderRadius: '50px',
                px: 5,
                py: 1.75,
                fontSize: '1.05rem',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: 'rgba(255,255,255,0.25)',
                color: '#fff',
                '&:hover': { borderColor: YELLOW, color: YELLOW, bgcolor: `${YELLOW}10` },
              }}
            >
              How It Works
            </Button>
          </Stack>

          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 4 }}>
            No credit card required · Free 14-day trial · Cancel anytime
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
